import React from 'react';
import { neutrals } from 'src/theme/colors';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { PrimaryButton } from 'src/editor-v3/components/buttons/primary-button/PrimaryButton';
import { BUTTON_LAYOUTS, BUTTON_SIZES, BUTTON_TYPES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { stylesDesktop, stylesMobile } from './PickerGoogle.style';
import { Icon } from '@photosi/rn-components/icon/Icon';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { BannerLoggedUser } from '@photosi/rn-components/modals/dynamic-modals/modal-upload-multisource/picker-google-photo/BannerLoggedUser';
import { PickerLoadingContent } from '@photosi/rn-components/modals/dynamic-modals/modal-upload-multisource/picker-loading-content/PickerLoadingContent';
import { usePickerGoogle } from './PickerGoogle.logic';
import i18n from 'src/app/i18n';
import { PickerGooglePhotoErrors } from '@photosi/rn-components/picker-google-photo/picker-google-photo-errors/PickerGooglePhotoErrors';
import { PickerProps } from '../Picker.types';

export const PickerGoogle = observer((props: PickerProps) => {
  const { focusId, focusGroup, focusOrder } = props;
  const {
    isDesktop,
    isLoadingLogin,
    isUserLogged,
    hasGivenAccessToPhoto,
    email,
    profilePic,
    handleLogin,
    handleChoosePhoto,
    handleLogout,
  } = usePickerGoogle(props);

  const styles = isDesktop ? stylesDesktop : stylesMobile;
  return (
    <View style={styles.details}>
      {isLoadingLogin ? (
        <>
          <PickerLoadingContent
            text="upload:loading-wait"
            customStyles={{
              loadingContentDetails: styles.loadingContentDetails,
              loadingContentText: styles.loadingContentText,
            }}
          />
          <TextLato style={styles.description}>{i18n.t('upload:loading-login-google')}</TextLato>
        </>
      ) : (
        <>
          <PickerGooglePhotoErrors />
          {isUserLogged && !!email ? (
            <View>
              <BannerLoggedUser
                profilePic={profilePic}
                email={email}
                handleLogout={handleLogout}
                backgroundColor={neutrals.white}
              />
            </View>
          ) : (
            <View style={{ width: 54, height: 54 }}>
              <Icon name={ICON_NAMES.GooglePhoto} />
            </View>
          )}
          <View style={styles.textWrapper}>
            <TextLato style={styles.title}>
              {isUserLogged
                ? i18n.t('modal-picker-google-photo:title-logged')
                : i18n.t('modal-picker-google-photo:title-upload')}
            </TextLato>
            <TextLato style={styles.description}>
              {isUserLogged
                ? i18n.t('modal-picker-google-photo:description-logged')
                : i18n.t('modal-picker-google-photo:description-upload')}
            </TextLato>
          </View>
          {isUserLogged ? (
            <PrimaryButton
              style={styles.button}
              layout={BUTTON_LAYOUTS.CENTER}
              onPress={handleChoosePhoto}
              disabled={!hasGivenAccessToPhoto}
              text={i18n.t('modal-picker-google-photo:choose-photo')}
              testID={modalTestID(TestIDs.CHOOSE_PHOTO, TestIDs.CHOOSE_GOOGLE_PHOTO)}
              focusId={focusId}
              focusOrder={focusOrder}
              focusGroup={focusGroup}
              size={BUTTON_SIZES.REGULAR}
            />
          ) : (
            <PrimaryButton
              style={styles.button}
              layout={BUTTON_LAYOUTS.CENTER}
              onPress={handleLogin}
              text={i18n.t('modal-picker-google-photo:login')}
              testID={modalTestID(TestIDs.CHOOSE_PHOTO, TestIDs.GOOGLE_PHOTO_LOGIN)}
              focusId={focusId}
              focusOrder={focusOrder}
              focusGroup={focusGroup}
              type={BUTTON_TYPES.ICON_LEFT}
              iconName={ICON_NAMES.GooglePhoto}
              size={BUTTON_SIZES.REGULAR}
            />
          )}
        </>
      )}
    </View>
  );
});
